import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../lib/api';
import { API_BASE_URL } from '../config/constants';
import { Edit, AlertCircle, Search, User, Filter, Plus, ImageIcon, Edit2, Trash2, GripVertical } from 'lucide-react';

interface Character {
  id: string;
  name: string;
  isPremium: boolean;
  isEnable: boolean;
  isReviewModeCharacter: boolean;
  posterImageUrl?: string;
  profileImageUrl?: string;
  allImages?: string[];
  sortOrder?: number | null;
  elevenlabsVoiceId?: string;
}

interface UpdateCharacterRequest {
  characterId: number;
  name?: string;
  isPremium?: boolean;
  isReviewModeCharacter?: boolean;
  isEnable?: boolean;
  sortOrder: number;
  elevenlabsVoiceId: string;
}

// Sortable Character Card Component
interface SortableCharacterCardProps {
  character: Character;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  getImageUrl: (path: string | undefined) => string;
}

const SortableCharacterCard: React.FC<SortableCharacterCardProps> = ({
  character,
  onEdit,
  onDelete,
  getImageUrl
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: character.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const profileImageUrl = getImageUrl(character.profileImageUrl);
  const posterImageUrl = getImageUrl(character.posterImageUrl);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 relative group"
    >
      <div className="relative aspect-square overflow-hidden">
        {/* Profile Image - Default */}
        <img
          src={profileImageUrl}
          alt={`${character.name} - Profile`}
          className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-0"
        />
        
        {/* Poster Image - Hover */}
        {posterImageUrl ? (
          <>
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <img
                src={posterImageUrl}
                alt={`${character.name} - Poster`}
                className="w-full h-full object-contain transition-all duration-300 scale-95 group-hover:scale-100"
              />
            </div>
            {/* Image Type Indicator */}
            <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-white text-xs font-medium">
                Poster
              </span>
            </div>
          </>
        ) : (
          /* No Poster Available Indicator */
          <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500/80 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="text-white text-xs font-medium">
              Poster Yok
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 p-2 bg-black/60 rounded-md cursor-grab active:cursor-grabbing touch-none select-none hover:bg-black/80 transition-colors"
          style={{ touchAction: 'none' }}
        >
          <GripVertical size={18} className="text-white" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-2">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-bold text-white truncate flex-1">{character.name}</h3>
            {posterImageUrl && (
              <div className="flex-shrink-0 p-1 bg-black/40 rounded-full" title="Hover ile poster resmi">
                <ImageIcon size={12} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex space-x-1">
              <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                character.isPremium ? 'bg-amber-500 text-white' : 'bg-gray-500 text-white'
              }`}>
                {character.isPremium ? 'P' : 'Ü'}
              </span>
              <span className={`px-1 py-0.5 rounded text-xs font-medium ${
                character.isEnable ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
                {character.isEnable ? 'A' : 'P'}
              </span>
              {character.isReviewModeCharacter && (
                <span className="px-1 py-0.5 rounded text-xs font-medium bg-purple-500 text-white">
                  İ
                </span>
              )}
              {character.sortOrder !== null && character.sortOrder !== undefined && (
                <span className="px-1 py-0.5 rounded text-xs font-medium bg-blue-500 text-white">
                  #{character.sortOrder}
                </span>
              )}
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => onEdit(character.id)}
                className="p-1 text-white hover:text-emerald-300 transition-colors duration-200"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={() => onDelete(character.id)}
                className="p-1 text-white hover:text-red-300 transition-colors duration-200"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CharacterList: React.FC = () => {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'premium' | 'free' | 'active' | 'inactive' | 'review' | 'non-review'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [isDragInProgress, setIsDragInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchCharacters();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchCharacters = async () => {
    try {
      const data = await api.get('/character/all');
      setCharacters(data);
    } catch (err) {
      setError('Karakterler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path: string | undefined) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_BASE_URL}${path}`;
  };

  const filteredCharacters = characters.filter(character => {
    // İsim araması
    if (searchTerm && !character.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Filtreler
    if (filter === 'premium' && !character.isPremium) return false;
    if (filter === 'free' && character.isPremium) return false;
    if (filter === 'active' && !character.isEnable) return false;
    if (filter === 'inactive' && character.isEnable) return false;
    if (filter === 'review' && !character.isReviewModeCharacter) return false;
    if (filter === 'non-review' && character.isReviewModeCharacter) return false;
    
    return true;
  }).sort((a, b) => {
    // Önce sortOrder'a göre sırala (null/undefined değerler en sona)
    if (a.sortOrder !== null && a.sortOrder !== undefined && b.sortOrder !== null && b.sortOrder !== undefined) {
      return a.sortOrder - b.sortOrder;
    }
    if (a.sortOrder !== null && a.sortOrder !== undefined) return -1;
    if (b.sortOrder !== null && b.sortOrder !== undefined) return 1;
    
    // sortOrder yoksa, aktif karakterleri üstte göster
    if (a.isEnable && !b.isEnable) return -1;
    if (!a.isEnable && b.isEnable) return 1;
    return 0;
  });

  const handleBulkUpdate = async (updatedCharacters: Character[]) => {
    try {
      const updateRequests: UpdateCharacterRequest[] = updatedCharacters.map((char, index) => ({
        characterId: parseInt(char.id),
        name: char.name,
        isPremium: char.isPremium,
        isReviewModeCharacter: char.isReviewModeCharacter,
        isEnable: char.isEnable,
        sortOrder: index + 1,
        elevenlabsVoiceId: char.elevenlabsVoiceId || ''
      }));

      await api.post('/character/updateBulk', updateRequests);
      fetchCharacters(); // Yenilenmiş veriyi al
      setSuccessMessage('Karakterler başarıyla güncellendi');
    } catch (err) {
      setError('Karakterler güncellenirken bir hata oluştu');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragInProgress(false);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredCharacters.findIndex((char) => char.id === active.id);
    const newIndex = filteredCharacters.findIndex((char) => char.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(filteredCharacters, oldIndex, newIndex);
      
      // sortOrder'ları güncelle
      const updatedCharacters = newOrder.map((char, index) => ({
        ...char,
        sortOrder: index + 1
      }));

      // Optimistic update - UI'ı hemen güncelle
      const allCharactersUpdated = characters.map(char => {
        const updated = updatedCharacters.find(u => u.id === char.id);
        return updated || char;
      });
      
      setCharacters(allCharactersUpdated);
      
      // Backend'e gönder
      handleBulkUpdate(updatedCharacters);
    }
  };

  const handleDelete = async () => {
    if (!selectedCharacterId) return;

    setLoading(true);
    try {
      await api.delete(`/dev/website/character/${selectedCharacterId}`);
      fetchCharacters();
      setShowDeleteConfirm(false);
      setSelectedCharacterId(null);
    } catch (err) {
      setError('Karakter silinirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <img 
            src="/logo192.png" 
            alt="Viora Logo" 
            className="w-12 h-12"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Karakterler</h1>
            <p className="text-sm text-gray-500">Tüm karakterleri görüntüleyin ve yönetin</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/create')}
          className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200"
        >
          <Plus className="mr-2" size={18} />
          Yeni Karakter
        </button>
      </div>

      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    </div>
  );

  if (error) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <img 
            src="/logo192.png" 
            alt="Viora Logo" 
            className="w-12 h-12"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Karakterler</h1>
            <p className="text-sm text-gray-500">Tüm karakterleri görüntüleyin ve yönetin</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/create')}
          className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200"
        >
          <Plus className="mr-2" size={18} />
          Yeni Karakter
        </button>
      </div>

      <div className="bg-red-50 p-6 rounded-lg mb-6 flex items-center">
        <AlertCircle className="text-red-500 mr-3" size={24} />
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <img 
            src="/logo192.png" 
            alt="Viora Logo" 
            className="w-12 h-12"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Karakterler</h1>
            <p className="text-sm text-gray-500">Tüm karakterleri görüntüleyin ve yönetin</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/create')}
          className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200"
        >
          <Plus className="mr-2" size={18} />
          Yeni Karakter
        </button>
      </div>

      {/* Arama ve Filtreleme */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Karakter ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400" size={20} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">Tümü</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
              <option value="premium">Premium</option>
              <option value="free">Ücretsiz</option>
              <option value="review">İnceleme</option>
              <option value="non-review">İnceleme Dışı</option>
            </select>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 p-4 rounded-lg flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 p-6 rounded-lg mb-6 flex items-center">
          <AlertCircle className="text-red-500 mr-3" size={24} />
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      ) : (
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          onDragStart={() => setIsDragInProgress(true)}
          autoScroll={false}
        >
          <SortableContext items={filteredCharacters.map(char => char.id)} strategy={rectSortingStrategy}>
            <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 ${
              isDragInProgress ? 'opacity-90' : ''
            } select-none`} style={{ touchAction: 'none' }}>
              {filteredCharacters.map((character) => (
                <SortableCharacterCard
                  key={character.id}
                  character={character}
                  onEdit={(id) => navigate(`/edit/${id}`)}
                  onDelete={(id) => {
                    setSelectedCharacterId(id);
                    setShowDeleteConfirm(true);
                  }}
                  getImageUrl={getImageUrl}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Karakteri Sil</h3>
            <p className="text-gray-600 mb-6">
              Bu karakteri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterList; 